import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LoginPage.css";
import APP_VERSION from "../version";

export default function CreateTripPage({ currentUser }) {
  const navigate = useNavigate();
  const [campgrounds, setCampgrounds] = useState([]);
  const [filteredCampgrounds, setFilteredCampgrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState(currentUser?.postcode || "4031");
  const [radius, setRadius] = useState("100");
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilters, setSourceFilters] = useState({
    ai: true,
    user: true,
    private: true,
  });

  useEffect(() => {
    const fetchAllCampsites = async () => {
      setLoading(true);
      try {
        // 🔹 Fetch AI-generated campsites
        const aiRes = await fetch("/api/chatgpt/campsites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ postcode, radius }),
        });
        const aiData = await aiRes.json();
  
        console.log("📮 Sending postcode to public API:", postcode);
  
        // 🔹 Fetch shared & private campsites
        const [publicRes, myRes] = await Promise.all([
          fetch("/api/locations/public", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ postcode, radius }),
          }),
          fetch("/api/locations/my", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ postcode, radius }),
          }),
        ]);
  
        const publicData = await publicRes.json();
        const myData = await myRes.json();
  
        // 🔹 Format AI results
        const aiFormatted = aiData.map((site) => ({
          name: site.name,
          distance: site.distance || "—",
          features: site.features,
          access: site.access,
          petFriendly: site.petFriendly === "Yes" ? "Yes" : "No",
          source: "ai",
          url: site.URL || site.url || "",  // 🟢 Handle both keys just in case
        }));
        
  
        // 🔹 Format shared user results
        const publicFormatted = publicData.map((site) => ({
          id: site.id,
          name: site.name,
          distance: site.distance || "—",
          features: site.features,
          access: site.drivetrain,
          petFriendly: site.pet_friendly ? "Yes" : "No",
          source: site.share_flag === false ? "private" : "user",
          url: site.campsite_url,
        }));
  
        // 🔹 Format private user-only results
        const privateFormatted = myData
          .filter((site) => site.share_flag === false)
          .map((site) => ({
            id: site.id,
            name: site.name,
            distance: site.distance || "—",
            features: site.features,
            access: site.drivetrain,
            petFriendly: site.pet_friendly ? "Yes" : "No",
            source: "private",
            url: site.campsite_url,
          }));
  
        const combined = [...publicFormatted, ...privateFormatted, ...aiFormatted];
  
        combined.sort((a, b) => {
          const aDist = parseFloat(a.distance);
          const bDist = parseFloat(b.distance);
          return isNaN(aDist) ? 1 : isNaN(bDist) ? -1 : aDist - bDist;
        });
  
        setCampgrounds(combined);
        setFilteredCampgrounds(combined);
      } catch (err) {
        console.error("❌ Error loading campsites:", err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchAllCampsites();
  }, [postcode, radius]);
  
  useEffect(() => {
    const filtered = campgrounds.filter((site) => {
      const matchesSearch = searchTerm === "" || (
        (site.name && site.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (site.features && site.features.join(", ").toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const matchesSource = sourceFilters[site.source];
      return matchesSearch && matchesSource;
    });

    setFilteredCampgrounds(filtered);
  }, [searchTerm, sourceFilters, campgrounds]);

  const toggleFilter = (type) => {
    setSourceFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img
          src="/searching-camper-image.png"
          alt="Searching Camper"
          style={{ width: "120px", margin: "0 auto 20px auto", display: "block" }}
        />
        <h2>Create a trip</h2>

        {/* 🔍 Search */}
        <input
          type="text"
          placeholder="Search campsites (name, feature, etc)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            margin: "10px 0 10px 0",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />

        {/* ✅ Source Filters */}
        <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginBottom: "16px" }}>
          {["ai", "user", "private"].map((type) => (
            <label key={type} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <input
                type="checkbox"
                checked={sourceFilters[type]}
                onChange={() => toggleFilter(type)}
              />
              {type === "ai" ? "AI results" : type === "user" ? "User results" : "Private results"}
            </label>
          ))}
        </div>

        {/* ✅ Radius/Postcode controls */}
        <div style={{ marginBottom: "20px", fontSize: "14px", color: "#555", textAlign: "center" }}>
          <span>Campsites within </span>
          <select
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            style={{ margin: "0 6px", padding: "4px", borderRadius: "6px" }}
          >
            <option value="50">50km</option>
            <option value="100">100km</option>
            <option value="200">200km</option>
          </select>
          <span> of postcode </span>
          <input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            style={{
              width: "70px",
              padding: "4px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              textAlign: "center",
            }}
          />
        </div>

        <button
          className="green-button"
          onClick={() => navigate("/add-location")}
          style={{ marginBottom: "20px", fontSize: "1em" }}
        >
          Add your own location
        </button>

        {loading ? (
          <p>Loading campgrounds...</p>
        ) : filteredCampgrounds.length === 0 ? (
          <div style={{ textAlign: "center" }}>
            <img src="/sad-camper-image.png" alt="No Trips" style={{ width: "160px", marginBottom: "10px" }} />
            <p style={{ fontStyle: "italic", color: "#777" }}>
              No campsites found matching your search or filters.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "center" }}>
            {filteredCampgrounds.map((site, idx) => (
              <div
              key={idx}
              onClick={() => navigate("/create-trip-details", { state: { site } })}
              style={{
                position: "relative",
                cursor: "pointer",
                backgroundColor: "#f2f2f2",
                padding: "12px 18px",
                borderRadius: "12px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                width: "100%",
                maxWidth: "480px",
                textAlign: "left",
                color: "#000",
                lineHeight: "1.2",
                overflow: "visible",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "6px", fontSize: "1.05em" }}>
                {site.name}
              </div>
              <div><strong>Distance:</strong> {site.distance}</div>
              <div><strong>Features:</strong> {Array.isArray(site.features) ? site.features.join(", ") : site.features}</div>
              <div><strong>Access:</strong> {site.access}</div>
              <div><strong>Pet Friendly:</strong> {site.petFriendly}</div>
              {site.url && (
                <div>
                  <strong>Website:</strong>{" "}
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()} // ✅ Prevents triggering parent onClick
                  >
                    {site.url.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
            
              {/* ✅ Top-right Source Badge */}
              <div
                style={{
                  position: "absolute",
                  top: "-12px",
                  right: "-12px",
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  padding: "4px 10px 4px 6px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 0 4px rgba(0,0,0,0.15)",
                  zIndex: 2,
                }}
              >
                {site.source === "ai" ? (
                  <>
                    <img src="/ai-camper-small.png" alt="AI Result" style={{ width: "24px", marginRight: "6px" }} />
                    AI Result
                  </>
                ) : site.source === "private" ? (
                  <>
                    <img src="/small-half-camper-shh.png" alt="Private Result" style={{ width: "24px", marginRight: "6px" }} />
                    Private Result
                  </>
                ) : (
                  <>
                    <img src="/small-half-camper.png" alt="User Result" style={{ width: "24px", marginRight: "6px" }} />
                    User Result
                  </>
                )}
              </div>
            </div>
            
            ))}
          </div>
        )}

        <button className="home-button" onClick={() => navigate("/")} style={{ marginTop: "30px", width: "100%" }}>
          Home
        </button>
        <div className="version-text" style={{ marginTop: "20px", fontSize: "12px", color: "#ccc", display: "flex", justifyContent: "space-between" }}>
          <span>Version: <strong>{APP_VERSION}</strong></span>
          <span>
            <Link to="/logout" className="contact-link" style={{ marginRight: "16px" }}>Logout</Link>
            <Link to="/contact" className="contact-link">Contact us</Link>
          </span>
        </div>
      </div>
    </div>
  );
}
